
import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Upload, AlertTriangle, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useFeatureBlocking } from '@/hooks/useFeatureBlocking';
import { RestrictedFeature } from '@/components/ui/RestrictedFeature';
import { ThinkingOrbLoader } from '@/components/ui/ThinkingOrbLoader';

/**
 * Modal de Importação de Contatos
 * 
 * O que faz:
 * - Exibe aviso sobre necessidade de WhatsApp conectado
 * - Permite usuário confirmar e iniciar importação
 * - Monitora progresso da importação em tempo real
 * - Exibe resultado final (sucesso/erro)
 * 
 * Onde é usado:
 * - ClientsPageHeader.tsx (botão "Importar Contatos")
 * 
 * Como funciona:
 * 1. Modal abre com aviso inicial
 * 2. Usuário confirma → chama Edge Function invoke-n8n-import
 * 3. Edge Function retorna log_id → inicia polling do status
 * 4. Polling verifica tabela import_contacts_logs até conclusão
 * 5. Exibe resultado final
 */

interface ImportContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ImportStatus = 'idle' | 'confirming' | 'importing' | 'success' | 'error';

export const ImportContactsModal: React.FC<ImportContactsModalProps> = ({
  isOpen,
  onClose
}) => {
  const [status, setStatus] = useState<ImportStatus>('idle');
  const [logId, setLogId] = useState<string | null>(null);
  const [importDetails, setImportDetails] = useState<{
    totalImported?: number;
    details?: string;
    errorMessage?: string;
  }>({});
  
  const { toast } = useToast();
  const { canImportContacts, subscriptionTier } = useFeatureBlocking();
  
  // Refs para gerenciar polling e estado de montagem
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  // Verificar se tem permissão para importar
  if (!canImportContacts) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-orange-600" />
              Recurso Premium
            </DialogTitle>
          </DialogHeader>
          
          <RestrictedFeature
            featureName="importação de contatos"
            planName={subscriptionTier}
            showUpgrade={true}
            isAdmin={false}
            hasOverride={false}
            onRequestAccess={() => {
              window.location.href = '/billing';
            }}
            variant="modal"
          />
        </DialogContent>
      </Dialog>
    );
  }

  // Gerenciar lifecycle do componente
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // Reset modal state quando abrir/fechar
  useEffect(() => {
    if (isOpen) {
      setStatus('idle');
      setLogId(null);
      setImportDetails({});
    }
  }, [isOpen]);

  /**
   * Inicia o processo de importação
   * Chama a Edge Function e começa monitoramento
   */
  const handleStartImport = async () => {
    setStatus('confirming');

    try {
      console.log('🚀 [ImportContactsModal] Iniciando importação...');

      // Chamar Edge Function para iniciar importação
      const { data, error } = await supabase.functions.invoke('invoke-n8n-import', {
        body: {}
      });

      if (error) {
        console.error('❌ [ImportContactsModal] Erro ao iniciar importação:', error);
        setStatus('error');
        setImportDetails({
          errorMessage: 'Erro ao conectar com o sistema de importação'
        });
        toast({
          variant: "destructive",
          title: "Erro na Importação",
          description: "Falha ao iniciar o processo de importação. Tente novamente."
        });
        return;
      }

      if (!data?.success || !data?.log_id) {
        console.error('❌ [ImportContactsModal] Resposta inválida:', data);
        setStatus('error');
        setImportDetails({
          errorMessage: data?.error || 'Resposta inválida do servidor'
        });
        toast({
          variant: "destructive",
          title: "Erro na Importação",
          description: data?.error || "Erro desconhecido"
        });
        return;
      }

      console.log(`✅ [ImportContactsModal] Importação iniciada! Log ID: ${data.log_id}`);
      
      setLogId(data.log_id);
      setStatus('importing');
      
      toast({
        title: "Importação Iniciada",
        description: "O processo de importação foi iniciado. Aguarde..."
      });

      // Iniciar monitoramento do progresso
      startPolling(data.log_id);

    } catch (error) {
      console.error('❌ [ImportContactsModal] Erro geral:', error);
      setStatus('error');
      setImportDetails({
        errorMessage: 'Erro interno. Tente novamente.'
      });
      toast({
        variant: "destructive",
        title: "Erro Inesperado",
        description: "Ocorreu um erro inesperado. Tente novamente."
      });
    }
  };

  /**
   * Monitora progresso da importação
   * Faz polling da tabela import_contacts_logs até conclusão
   */
  const startPolling = (logIdToMonitor: string) => {
    console.log(`👀 [ImportContactsModal] Iniciando monitoramento do log: ${logIdToMonitor}`);
    
    // Limpar intervalo anterior se existir
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    let pollCount = 0;
    const maxPolls = 200; // 200 polls * 3 segundos = 10 minutos máximo
    
    pollingIntervalRef.current = setInterval(async () => {
      pollCount++;
      console.log(`🔍 [ImportContactsModal] Poll #${pollCount} para log: ${logIdToMonitor}`);
      
      // Verificar timeout
      if (pollCount >= maxPolls) {
        console.log('⏱️ [ImportContactsModal] Timeout atingido');
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        if (isMountedRef.current) {
          setStatus('error');
          setImportDetails({
            errorMessage: 'Timeout: A importação demorou mais que o esperado'
          });
        }
        return;
      }
      
      try {
        const { data: logData, error } = await supabase
          .from('import_contacts_logs')
          .select('status, total_imported, details, error_message, finished_at')
          .eq('id', logIdToMonitor)
          .single();

        if (error) {
          console.error('❌ [ImportContactsModal] Erro ao buscar log:', error);
          return;
        }

        console.log(`📊 [ImportContactsModal] Status atual: ${logData.status}`, logData);

        // Se ainda está em progresso, continuar polling
        if (logData.status === 'pending' || logData.status === 'running') {
          console.log('⏳ [ImportContactsModal] Importação ainda em progresso...');
          return;
        }

        // Processo finalizado - parar polling imediatamente
        console.log('🛑 [ImportContactsModal] Status finalizado detectado, parando polling');
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        // Atualizar estado apenas se componente ainda estiver montado
        if (!isMountedRef.current) {
          console.log('⚠️ [ImportContactsModal] Componente desmontado, ignorando atualização');
          return;
        }

        if (logData.status === 'success') {
          console.log('✅ [ImportContactsModal] Importação concluída com sucesso!');
          setStatus('success');
          setImportDetails({
            totalImported: logData.total_imported,
            details: logData.details
          });
          
          toast({
            title: "Importação Concluída!",
            description: `${logData.total_imported || 0} contatos foram importados com sucesso.`
          });
          
        } else if (logData.status === 'error') {
          console.log('❌ [ImportContactsModal] Importação falhou:', logData.error_message);
          setStatus('error');
          setImportDetails({
            errorMessage: logData.error_message || 'Erro desconhecido durante a importação'
          });
          
          toast({
            variant: "destructive",
            title: "Erro na Importação",
            description: "A importação falhou. Verifique os detalhes."
          });
        }

      } catch (pollError) {
        console.error('❌ [ImportContactsModal] Erro no polling:', pollError);
      }
    }, 3000); // Verificar a cada 3 segundos
  };

  /**
   * Fecha modal e reseta estado
   */
  const handleClose = () => {
    // Limpar intervalo de polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    setStatus('idle');
    setLogId(null);
    setImportDetails({});
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-green-600" />
            Importar Contatos
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Estado Inicial - Aviso sobre WhatsApp */}
          {status === 'idle' && (
            <>
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Importante:</strong> Certifique-se de que o WhatsApp já está conectado ao CRM antes de prosseguir com a importação.
                </AlertDescription>
              </Alert>

              <div className="text-sm text-gray-600">
                <p>A importação irá:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Buscar contatos do WhatsApp conectado</li>
                  <li>Importar apenas contatos novos (não duplicados)</li>
                  <li>Atualizar sua base de contatos automaticamente</li>
                </ul>
              </div>

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={handleClose}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleStartImport}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Prosseguir com Importação
                </Button>
              </div>
            </>
          )}

          {/* Estado Confirmando/Importando */}
          {(status === 'confirming' || status === 'importing') && (
            <>
              <div className="flex flex-col items-center justify-center py-8">
                <ThinkingOrbLoader state="working" size={64} />
                <h3 className="font-medium text-lg mb-2">
                  {status === 'confirming' ? 'Iniciando importação...' : 'Importando contatos...'}
                </h3>
                <p className="text-sm text-gray-600 text-center">
                  {status === 'confirming' 
                    ? 'Conectando com o sistema de importação...'
                    : 'Aguarde enquanto seus contatos são importados. Isso pode levar alguns minutos.'
                  }
                </p>
              </div>
            </>
          )}

          {/* Estado de Sucesso */}
          {status === 'success' && (
            <>
              <div className="flex flex-col items-center justify-center py-8">
                <CheckCircle className="h-12 w-12 text-green-600 mb-4" />
                <h3 className="font-medium text-lg mb-2 text-green-800">
                  Importação Concluída!
                </h3>
                <p className="text-sm text-gray-600 text-center">
                  {importDetails.totalImported !== undefined 
                    ? `${importDetails.totalImported} contatos foram importados com sucesso.`
                    : 'A importação foi concluída com sucesso.'
                  }
                </p>
                {importDetails.details && (
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    {importDetails.details}
                  </p>
                )}
              </div>

              <div className="flex justify-end">
                <Button onClick={handleClose} className="bg-green-600 hover:bg-green-700">
                  Fechar
                </Button>
              </div>
            </>
          )}

          {/* Estado de Erro */}
          {status === 'error' && (
            <>
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Erro na Importação:</strong> {importDetails.errorMessage}
                </AlertDescription>
              </Alert>

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={handleClose}>
                  Fechar
                </Button>
                <Button onClick={() => setStatus('idle')} className="bg-green-600 hover:bg-green-700">
                  Tentar Novamente
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
